// CreateStepType — wizard pane 1.
// Substrate: design/add.html lines 211-314 — `<section data-pane="1">`.
//
// Fields (all backend-real per PropertyWriteSerializer):
//   property_type, deal_type, title, price, currency,
//   has_housing_program, has_recovery_program,
//   cooperation_offer (DEALS-CREATION-FLOW-B2).
//
// Self-contained per RIC-1 hybrid rule: the canonical .radio-card /
// .toggle-card / .check-card field markup is authored locally (NOT
// imported from features/editRealty). modernize.css classes reused
// verbatim — no new styles.

import CooperationOfferFields from "../../realty/widgets/CooperationOfferFields";

const PROPERTY_TYPE_OPTIONS = [
  { value: "apartment", label: "Квартира", icon: "fa-solid fa-building", hint: "Житлова" },
  { value: "house", label: "Будинок", icon: "fa-solid fa-house-chimney", hint: "Житлова" },
  { value: "land", label: "Земельна ділянка", icon: "fa-solid fa-mountain-sun", hint: "Земля" },
  { value: "commercial", label: "Комерційна", icon: "fa-solid fa-store", hint: "Комерційна" },
  { value: "industrial", label: "Промислова", icon: "fa-solid fa-industry", hint: "Комерційна" },
];

const DEAL_TYPE_OPTIONS = [
  { value: "sale", label: "Продаж", icon: "fa-solid fa-tag" },
  { value: "rent", label: "Оренда", icon: "fa-solid fa-key" },
];

const CURRENCY_OPTIONS = [
  { value: "USD", label: "USD — Долар" },
  { value: "EUR", label: "EUR — Євро" },
  { value: "UAH", label: "UAH — Гривня" },
];

function CreateStepType({ active, values, onChange, fieldErrors = {}, typeLocked = false }) {
  const setField = (key, value) => {
    if (typeLocked && (key === "property_type" || key === "deal_type")) return;
    onChange({ ...values, [key]: value });
  };

  return (
    <section
      className={active ? "ap-pane is-active" : "ap-pane"}
      data-pane="1"
      aria-labelledby="ap-pane-1"
      hidden={!active}
    >
      <header className="ap-pane__head">
        <h2 id="ap-pane-1">
          <i className="fa-solid fa-tag" aria-hidden="true" />
          Тип нерухомості та угоди
        </h2>
        <p>
          Виберіть тип об'єкта і тип угоди — від цього залежить, які поля
          показати на кроці «Характеристики».
        </p>
        {typeLocked ? (
          <p className="form-hint">
            Тип і угода зафіксовані після створення чернетки. Щоб змінити їх,
            створіть нове оголошення.
          </p>
        ) : null}
      </header>

      <fieldset className="ap-fieldset">
        <legend>
          Тип нерухомості <span className="req">*</span>
        </legend>
        <div className="ap-radio-grid" role="radiogroup" aria-label="Тип нерухомості">
          {PROPERTY_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value} className="radio-card">
              <input
                type="radio"
                name="property_type"
                value={opt.value}
                checked={values.property_type === opt.value}
                disabled={typeLocked}
                onChange={() => setField("property_type", opt.value)}
              />
              <span className="radio-card__icon">
                <i className={opt.icon} aria-hidden="true" />
              </span>
              <span className="radio-card__label">{opt.label}</span>
              <span className="radio-card__hint">{opt.hint}</span>
            </label>
          ))}
        </div>
        {fieldErrors.property_type ? (
          <small className="form-hint text-danger">
            {fieldErrors.property_type}
          </small>
        ) : null}
      </fieldset>

      <fieldset className="ap-fieldset">
        <legend>
          Тип угоди <span className="req">*</span>
        </legend>
        <div className="ap-toggle-group" role="radiogroup" aria-label="Тип угоди">
          {DEAL_TYPE_OPTIONS.map((opt) => (
            <label key={opt.value} className="toggle-card">
              <input
                type="radio"
                name="deal_type"
                value={opt.value}
                checked={values.deal_type === opt.value}
                disabled={typeLocked}
                onChange={() => setField("deal_type", opt.value)}
              />
              <i className={opt.icon} aria-hidden="true" />
              {opt.label}
            </label>
          ))}
        </div>
        {fieldErrors.deal_type ? (
          <small className="form-hint text-danger">
            {fieldErrors.deal_type}
          </small>
        ) : null}
      </fieldset>

      <div className="ap-row">
        <div className="ap-field ap-field--wide">
          <label htmlFor="prop-title">
            Заголовок <span className="req">*</span>
          </label>
          <input
            id="prop-title"
            name="title"
            type="text"
            className="form-control"
            maxLength={255}
            placeholder="Напр.: Світла 2-к квартира в центрі"
            value={values.title || ""}
            onChange={(e) => setField("title", e.target.value)}
          />
          <small className="form-hint">
            До 255 символів. Це назва картки на сайті.
          </small>
          {fieldErrors.title ? (
            <small className="form-hint text-danger">{fieldErrors.title}</small>
          ) : null}
        </div>
      </div>

      <div className="ap-row ap-row--2">
        <div className="ap-field">
          <label htmlFor="prop-price">
            Ціна <span className="req">*</span>
          </label>
          <div className="ap-input-group">
            <input
              id="prop-price"
              name="price"
              type="number"
              className="form-control"
              min="0"
              step="0.01"
              placeholder="0"
              value={values.price ?? ""}
              onChange={(e) => setField("price", e.target.value)}
            />
            <select
              id="prop-currency"
              name="currency"
              className="form-select"
              aria-label="Валюта"
              value={values.currency || "USD"}
              onChange={(e) => setField("currency", e.target.value)}
            >
              {CURRENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <small className="form-hint">До 12 цифр, не від'ємна.</small>
          {fieldErrors.price ? (
            <small className="form-hint text-danger">{fieldErrors.price}</small>
          ) : null}
          {fieldErrors.currency ? (
            <small className="form-hint text-danger">
              {fieldErrors.currency}
            </small>
          ) : null}
        </div>
      </div>

      <fieldset className="ap-fieldset">
        <legend>Державні програми</legend>
        <div className="ap-checkbox-row">
          <label className="check-card">
            <input
              type="checkbox"
              name="has_housing_program"
              checked={Boolean(values.has_housing_program)}
              onChange={(e) => setField("has_housing_program", e.target.checked)}
            />
            <span className="check-card__icon">
              <i className="fa-solid fa-house-flag" aria-hidden="true" />
            </span>
            <span className="check-card__body">
              <strong>єОселя</strong>
              <small>Державна житлова програма</small>
            </span>
          </label>
          <label className="check-card">
            <input
              type="checkbox"
              name="has_recovery_program"
              checked={Boolean(values.has_recovery_program)}
              onChange={(e) => setField("has_recovery_program", e.target.checked)}
            />
            <span className="check-card__icon">
              <i className="fa-solid fa-rotate" aria-hidden="true" />
            </span>
            <span className="check-card__body">
              <strong>єВідновлення</strong>
              <small>Державна програма відновлення</small>
            </span>
          </label>
        </div>
      </fieldset>

      <CooperationOfferFields
        values={values}
        onChange={onChange}
        fieldErrors={fieldErrors}
        idPrefix="create-coop"
      />
    </section>
  );
}

export default CreateStepType;
